// modules/heroNewsPattern.js
export default function initHeroNewsPattern(root = document) {
    const c = root.querySelector('[data-hero-news]');
    if (!c) return;

    const ctx = c.getContext('2d');
    const DPR = Math.max(1, Math.min(2, window.devicePixelRatio || 1));

    /* дизайн */
    const COL          = '#B8BDC3';
    const FILL_ALPHA   = 0.30;
    const STROKE_ALPHA = 0.30;
    const STROKE_W_CSS = 2;
    const LINE_ALPHA   = 0.20;

    /* геометрія */
    const DESKTOP_FIXED_R = 127; // ⬅️ фіксований радіус на десктопі
    const SPREAD_X_K = 1.00;     // L/R сходяться в центрі "боками"
    const SPREAD_Y_K = 0.78;     // T/B як у макеті

    /* мобільний клемп */
    const MOBILE_BP    = 1200;
    const MOBILE_MAX_R = 49;

    const TABLET_BP     = 1330;   // < 1330px
    const TABLET_DIAM   = 170;    // диаметр по ТЗ
    const TABLET_R      = TABLET_DIAM / 2; // 85

    /* repel */
    const SEGMENTS    = 220;
    const INFLUENCE_R = 127;
    const STRENGTH    = 0.22;
    const SMOOTH      = 0.18;

    /* pointer */
    const P = { gx: 0, gy: 0, active: false };
    const setPtr = e => {
        const r = c.getBoundingClientRect();
        P.gx = e.clientX - r.left;
        P.gy = e.clientY - r.top;
        P.active = true;
    };
    c.addEventListener('pointermove', setPtr, { passive: true });
    c.addEventListener('pointerenter', setPtr, { passive: true });
    c.addEventListener('pointerleave', () => (P.active = false), { passive: true });

    /* offscreens */
    const fillBuf = document.createElement('canvas');
    const cutBuf  = document.createElement('canvas');
    const ftx    = fillBuf.getContext('2d');
    const ctxCut = cutBuf.getContext('2d');

    /* state */
    let W = 0, H = 0, midY = 0;
    let R = DESKTOP_FIXED_R, spX = 0, spY = 0, cx = 0, cy = 0;
    let rings = [];
    const TAU = Math.PI * 2;

    /* utils */
    function cssPx(el, name, def) {
        const v = getComputedStyle(el).getPropertyValue(name).trim();
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : def;
    }

    /* responsive center from CSS vars */
    function computeVisualCenterY() {
        const baseH = cssPx(c, '--news-base-h', 423);
        const bTop  = cssPx(c, '--news-bleed-top', 40);
        return bTop + baseH / 2;
    }

    /* resize */
    function resize() {
        const cssW = c.clientWidth  || 1920;
        const cssH = c.clientHeight || 423;

        W = Math.round(cssW * DPR);
        H = Math.round(cssH * DPR);
        c.width = W; c.height = H;
        c.style.width  = cssW + 'px';
        c.style.height = cssH + 'px';

        fillBuf.width = W; fillBuf.height = H;
        cutBuf.width  = W; cutBuf.height  = H;

        const visualCenterY = computeVisualCenterY();
        midY = Math.round(visualCenterY * DPR);
        c.__centerYVisual = visualCenterY;

        rebuild();
    }

    // спостерігачі за розмірами
    const ro = new ResizeObserver(resize);
    ro.observe(c);
    const art = c.closest('.team-hero__art');
    if (art) ro.observe(art);

    // додаткові тригери
    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('orientationchange', resize, { passive: true });
    const mq = window.matchMedia(`(max-width: ${MOBILE_BP - 1}px)`);
    mq.addEventListener?.('change', resize);
    // Fallback для старих браузерів
    mq.addListener?.(resize);

    resize();

    /* build rings */
    function rebuild() {
        const vw = window.innerWidth || document.documentElement.clientWidth || 0;

        // радиус: <1200 → мобильный, 1200–1329 → диаметр 170, ≥1330 → десктоп 127
        if (vw < MOBILE_BP) {
            R = MOBILE_MAX_R;            // (диаметр ~98)
        } else if (vw < TABLET_BP) {
            R = TABLET_R;                // (диаметр 170)
        } else {
            R = DESKTOP_FIXED_R;         // (диаметр 254)
        }

        spX = R * SPREAD_X_K;
        spY = R * SPREAD_Y_K;

        cx  = (W / DPR) * 0.5;
        cy  = c.__centerYVisual ?? (H / DPR) * 0.5;

        const C = [
            { name: 'L', x: cx - spX, y: cy },
            { name: 'R', x: cx + spX, y: cy },
            { name: 'T', x: cx,       y: cy - spY },
            { name: 'B', x: cx,       y: cy + spY },
        ];

        rings = C.map(cc => {
            const nodes = [];
            for (let i = 0; i < SEGMENTS; i++) {
                const t  = (i / SEGMENTS) * TAU;
                const x0 = cc.x * DPR + Math.cos(t) * R * DPR;
                const y0 = cc.y * DPR + Math.sin(t) * R * DPR;
                nodes.push({ x0, y0, x: x0, y: y0 });
            }
            return { name: cc.name, cx: cc.x * DPR, cy: cc.y * DPR, r: R * DPR, nodes };
        });
    }

    /* deform + stroke */
    function deformAndStroke() {
        const px = P.gx * DPR, py = P.gy * DPR, radius = INFLUENCE_R * DPR;

        ctx.save();
        ctx.strokeStyle = COL;
        ctx.globalAlpha = STROKE_ALPHA;
        ctx.lineWidth   = Math.max(1, STROKE_W_CSS * DPR);
        ctx.lineJoin = 'round'; ctx.lineCap = 'round';

        for (const ring of rings) {
            for (const n of ring.nodes) {
                let tx = n.x0, ty = n.y0;
                if (P.active) {
                    const dx = n.x0 - px, dy = n.y0 - py;
                    const dist = Math.hypot(dx, dy) || 1e-4;
                    if (dist < radius) {
                        const fall = 1 - dist / radius;
                        const mag  = STRENGTH * (fall * fall) * radius;
                        tx += (dx / dist) * mag;
                        ty += (dy / dist) * mag;
                    }
                }
                n.x += (tx - n.x) * SMOOTH;
                n.y += (ty - n.y) * SMOOTH;
            }
            const nodes = ring.nodes;
            ctx.beginPath();
            ctx.moveTo(nodes[0].x, nodes[0].y);
            for (let i = 1; i < nodes.length; i++) ctx.lineTo(nodes[i].x, nodes[i].y);
            ctx.closePath();
            ctx.stroke();
        }
        ctx.restore();
    }

    /* fill T/B with exact hole (boolean T ∩ B) */
    function drawFillWithExactHole() {
        const T = rings.find(r => r.name === 'T');
        const B = rings.find(r => r.name === 'B');
        if (!T || !B) return;

        ftx.clearRect(0,0,W,H);
        ftx.save();
        ftx.fillStyle = COL;
        ftx.globalAlpha = FILL_ALPHA;

        ftx.beginPath();
        ftx.moveTo(T.nodes[0].x, T.nodes[0].y);
        for (let i = 1; i < T.nodes.length; i++) ftx.lineTo(T.nodes[i].x, T.nodes[i].y);
        ftx.closePath(); ftx.fill();

        ftx.beginPath();
        ftx.moveTo(B.nodes[0].x, B.nodes[0].y);
        for (let i = 1; i < B.nodes.length; i++) ftx.lineTo(B.nodes[i].x, B.nodes[i].y);
        ftx.closePath(); ftx.fill();
        ftx.restore();

        ctxCut.clearRect(0,0,W,H);
        ctxCut.save();
        ctxCut.fillStyle = '#000';

        ctxCut.globalCompositeOperation = 'source-over';
        ctxCut.beginPath();
        ctxCut.moveTo(T.nodes[0].x, T.nodes[0].y);
        for (let i = 1; i < T.nodes.length; i++) ctxCut.lineTo(T.nodes[i].x, T.nodes[i].y);
        ctxCut.closePath(); ctxCut.fill();

        ctxCut.globalCompositeOperation = 'destination-in';
        ctxCut.beginPath();
        ctxCut.moveTo(B.nodes[0].x, B.nodes[0].y);
        for (let i = 1; i < B.nodes.length; i++) ctxCut.lineTo(B.nodes[i].x, B.nodes[i].y);
        ctxCut.closePath(); ctxCut.fill();
        ctxCut.restore();

        ftx.save();
        ftx.globalCompositeOperation = 'destination-out';
        ftx.drawImage(cutBuf, 0, 0);
        ftx.restore();

        ctx.drawImage(fillBuf, 0, 0);
    }
    const LINE_COL = '#C6CBD1';
    /* center line */
    function drawLine() {
        ctx.save();
        ctx.globalAlpha = LINE_ALPHA;
        ctx.fillStyle   = LINE_COL;   // было '#FFFFFF'
        const lh = Math.max(1, Math.round(1.3333 * DPR));
        ctx.fillRect(0, midY - Math.floor(lh / 2), W, lh);
        ctx.restore();
    }

    function frame() {
        ctx.clearRect(0, 0, W, H);
        drawFillWithExactHole();
        deformAndStroke();
        drawLine();
        requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
}
